<script setup>
import ContactUs from "@/Components/Web/ContactUs.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
</script>

<template>
  <div>
    <!-- ====== Footer Section Start -->
    <footer class="relative z-10 py-8 border-t border-gray-3">
      <!-- Contact Us Section -->
      <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 mb-10">
        <h3 class="mb-6 text-xl font-semibold text-center">
          {{ t("footer.getInTouch") }}
        </h3>
        <div class="contact-us-wrapper">
          <ContactUs />
        </div>
      </div>

      <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
        <div
          class="flex flex-col flex-wrap items-center justify-center gap-4 lg:gap-0 lg:flex-row lg:justify-between"
        >
          <div>
            <p class="text-custom-sm mobile-font">
              &copy; 2025 {{ $page.props.apps.bussinesname }}
            </p>
          </div>

          <!-- Social Media Icons -->
          <div class="flex items-center gap-4">
            <a
              v-if="$page.props.apps.linkfb"
              :href="$page.props.apps.linkfb"
              target="_blank"
              rel="noopener noreferrer"
              class="p-2 text-gray-600 transition-colors duration-200 rounded-full hover:text-blue-600 hover:bg-gray-100"
              title="Facebook"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
            </a>

            <a
              v-if="$page.props.apps.linkinstagram"
              :href="$page.props.apps.linkinstagram"
              target="_blank"
              rel="noopener noreferrer"
              class="p-2 text-gray-600 transition-colors duration-200 rounded-full hover:text-pink-600 hover:bg-gray-100"
              title="Instagram"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-2.504 0-4.536-2.033-4.536-4.537s2.032-4.536 4.536-4.536 4.537 2.032 4.537 4.536-2.033 4.537-4.537 4.537zm7.119 0c-2.504 0-4.536-2.033-4.536-4.537s2.032-4.536 4.536-4.536 4.537 2.032 4.537 4.536-2.033 4.537-4.537 4.537z"
                />
              </svg>
            </a>

            <a
              v-if="$page.props.apps.linkyoutube"
              :href="$page.props.apps.linkyoutube"
              target="_blank"
              rel="noopener noreferrer"
              class="p-2 text-gray-600 transition-colors duration-200 rounded-full hover:text-red-600 hover:bg-gray-100"
              title="YouTube"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                />
              </svg>
            </a>

            <a
              v-if="$page.props.apps.linkemail"
              :href="`mailto:${$page.props.apps.linkemail}`"
              class="p-2 text-gray-600 transition-colors duration-200 rounded-full hover:text-green-600 hover:bg-gray-100"
              title="Email"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                />
              </svg>
            </a>

            <a
              v-if="$page.props.apps.linkweb"
              :href="$page.props.apps.linkweb"
              target="_blank"
              rel="noopener noreferrer"
              class="p-2 text-gray-600 transition-colors duration-200 rounded-full hover:text-purple-600 hover:bg-gray-100"
              title="Website"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
                />
              </svg>
            </a>
          </div>

          <div>
            <p class="text-gray-600 text-custom-sm mobile-font">
              {{ t("footer.craftedBy") }}&nbsp;
              <a
                href="https://www.facebook.com/anselmus"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-colors duration-200 hover:text-gray-900"
                >Tim Humas FEB</a
              >
            </p>
          </div>
        </div>
      </div>
    </footer>
    <!-- ====== Footer Section End -->
  </div>
</template>

<style scoped>
/* Mobile-specific font styling */
.mobile-font {
  /* Mobile font (default) */
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
  font-weight: 500;
}

/* Desktop font styling */
@media (min-width: 640px) {
  .mobile-font {
    font-family: "Roboto", "Segoe UI", -apple-system, BlinkMacSystemFont,
      sans-serif;
    font-weight: inherit;
  }
}

/* Social media icons styling */
.social-icon {
  transition: all 0.3s ease;
}

.social-icon:hover {
  transform: translateY(-2px);
}

/* Mobile responsive adjustments for social icons */
@media (max-width: 1023px) {
  .flex-col .flex {
    order: 2;
    margin: 0.5rem 0;
  }

  .flex-col > div:first-child {
    order: 1;
  }

  .flex-col > div:last-child {
    order: 3;
  }
}

/* Ensure proper spacing on mobile */
@media (max-width: 640px) {
  .gap-4 {
    gap: 0.75rem;
  }

  .p-2 {
    padding: 0.375rem;
  }

  .w-5 {
    width: 1.125rem;
    height: 1.125rem;
  }
}

/* Contact Us section styling */
.contact-us-wrapper {
  max-width: 100%;
  overflow: hidden;
}

h3.text-xl {
  color: #b8860b;
}
</style>
